import Markdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sparkles, Monitor, Smartphone, Globe } from 'lucide-react';
import {
  ReleaseNoteCategory,
  ReleaseNotePlatform,
  type ReleaseDto,
  type ReleaseNoteItemDto,
} from '../types';
import { CategoryBadge } from './CategoryBadge';

interface ReleaseNotesListProps {
  releases: ReleaseDto[];
}

function groupByCategory(notes: ReleaseNoteItemDto[]) {
  const groups = new Map<ReleaseNoteCategory, ReleaseNoteItemDto[]>();
  for (const note of notes) {
    const existing = groups.get(note.category) || [];
    existing.push(note);
    groups.set(note.category, existing);
  }
  return [...groups.entries()].sort(([a], [b]) => a - b);
}

function splitByPlatform(notes: ReleaseNoteItemDto[]) {
  const web: ReleaseNoteItemDto[] = [];
  const mobile: ReleaseNoteItemDto[] = [];
  const other: ReleaseNoteItemDto[] = [];

  for (const note of notes) {
    if (note.platform === ReleaseNotePlatform.All || note.platform === ReleaseNotePlatform.Api) {
      other.push(note);
    } else if (note.platform === ReleaseNotePlatform.Web) {
      web.push(note);
    } else if (note.platform === ReleaseNotePlatform.Mobile) {
      mobile.push(note);
    }
  }

  return { web, mobile, other };
}

function CategoryGroup({ notes }: { notes: ReleaseNoteItemDto[] }) {
  return (
    <>
      {groupByCategory(notes).map(([category, items]) => (
        <div key={category}>
          <h5 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <CategoryBadge category={category} />
            <span className="text-muted-foreground">({items.length})</span>
          </h5>
          <ul className="list-disc ml-5 space-y-1">
            {items.map((note) => (
              <li key={note.id} className="text-sm pl-1">
                {note.description}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export function ReleaseNotesList({ releases }: ReleaseNotesListProps) {
  if (releases.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No release notes available yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {releases.map((release) => {
        const { web, mobile, other } = splitByPlatform(release.notes);
        const platformSections = [
          ...(web.length > 0 ? ['web'] : []),
          ...(mobile.length > 0 ? ['mobile'] : []),
          ...(other.length > 0 ? ['other'] : []),
        ] as const;

        return (
          <Card key={release.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm font-mono">
                    Web {release.webVersion}
                  </Badge>
                  <Badge variant="secondary" className="text-sm font-mono">
                    Mobile {release.mobileVersion}
                  </Badge>
                  <CardTitle className="text-lg">{release.title}</CardTitle>
                </div>
                <span className="text-sm text-muted-foreground">
                  {new Date(release.releaseDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              {release.summary && (
                <p className="text-sm text-muted-foreground mt-1">
                  {release.summary}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {release.highlights.length === 0 &&
              release.notes.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No changes listed.
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Highlights — announcement-style blocks */}
                  {release.highlights.length > 0 && (
                    <>
                      {release.highlights.map((highlight) => (
                        <div
                          key={highlight.id}
                          className="rounded-lg border bg-muted/30 p-4"
                        >
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-warning" />
                            Introducing: {highlight.moduleName}
                          </h4>
                          <div className="text-sm [&_strong]:font-semibold [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:mb-2 [&_a]:text-primary [&_a]:underline [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1">
                            <Markdown>{highlight.content}</Markdown>
                          </div>
                        </div>
                      ))}
                      {release.notes.length > 0 && <Separator />}
                    </>
                  )}

                  {/* Change items — grouped by platform */}
                  {web.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        Web
                        <span className="font-mono text-muted-foreground font-normal">
                          v{release.webVersion}
                        </span>
                      </h4>
                      <div className="ml-1 space-y-3">
                        <CategoryGroup notes={web} />
                      </div>
                    </div>
                  )}

                  {platformSections.indexOf('web') >= 0 &&
                    platformSections.indexOf('mobile') >= 0 && <Separator />}

                  {mobile.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        Mobile
                        <span className="font-mono text-muted-foreground font-normal">
                          v{release.mobileVersion}
                        </span>
                      </h4>
                      <div className="ml-1 space-y-3">
                        <CategoryGroup notes={mobile} />
                      </div>
                    </div>
                  )}

                  {other.length > 0 &&
                    (web.length > 0 || mobile.length > 0) && <Separator />}

                  {other.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Other
                      </h4>
                      <div className="ml-1 space-y-3">
                        <CategoryGroup notes={other} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
